// ---------------------------------------------------------------------------
// Supplement Facts data for the new 9P single-strain formula.
//
// Each SKU renders as an HTML "Supplement Facts" card (see Supplement9PCard.js)
// instead of a flat PNG, so the text stays crisp and readable at any size.
//
// All 13 SKUs carry real values.
// Empty string values render as "—" in the table.
//
// `color` is the SKU's brand colour. The card derives its whole palette from it
// (header gradient, active-strain box, accent text). Omit it and the card falls
// back to the default purple.
//
// Thai rendering is derived, not duplicated:
//   * the SKU code is transliterated letter-by-letter (thai_sku_code)
//   * the nutrition NUMBERS are reused, only their units are swapped (thai_units)
//   * every SKU shares one carrier blend, so one Thai ingredient string covers all
//   * only the active strain name needs a per-SKU Thai entry (thai_active_strain)
// ---------------------------------------------------------------------------

// Thai letter names, used to transliterate a SKU code: A01-BBI -> เอ01-บีบีไอ.
// Digits and punctuation pass through unchanged.
const THAI_LETTER_NAMES = {
  A: 'เอ', B: 'บี', C: 'ซี', D: 'ดี', E: 'อี', F: 'เอฟ', G: 'จี',
  H: 'เอช', I: 'ไอ', J: 'เจ', K: 'เค', L: 'แอล', M: 'เอ็ม', N: 'เอ็น',
  O: 'โอ', P: 'พี', Q: 'คิว', R: 'อาร์', S: 'เอส', T: 'ที', U: 'ยู',
  V: 'วี', W: 'ดับเบิลยู', X: 'เอ็กซ์', Y: 'วาย', Z: 'แซด'
};

export const thai_sku_code = (sku) =>
  String(sku || '')
    .split('')
    .map((ch) => THAI_LETTER_NAMES[ch.toUpperCase()] || ch)
    .join('');

// Measurement units, English -> Thai. The numbers themselves are unchanged, so
// nutrition only has to be entered once.
export const thai_units = {
  Kcal: 'กิโลแคลอรี',
  kcal: 'กิโลแคลอรี',
  g: 'กรัม',
  mg: 'มิลลิกรัม'
};

// Every SKU uses the same carrier blend, so the Thai "other ingredients" text is
// shared. (The active strain is listed separately, above this line on the card.)
export const thai_other_ingredients =
  'แล็กโทบาซิลลัส แพลนทารัม (ถูกทำให้ตายด้วยความร้อน), ' +
  'กาแลคโตโอลิโกแซ็กคาไรด์ (GOS), อินูลิน, ฟรุกโตโอลิโกแซ็กคาไรด์ (FOS), ' +
  'ซิลิคอนไดออกไซด์, ใยอาหารจากมันสำปะหลัง';

export const thai_serving_size = '1 ซอง (2.0 กรัม)';

// Active strain names in Thai, per SKU.
// Genus transliterations are บิฟิโดแบคทีเรียม / แล็กโทบาซิลลัส. The Lactobacillus
// entries and A03-BLA were spelling-checked and corrected for print; the
// Bifidobacterium species names (A01-A04) still follow the derived spellings.
export const thai_active_strain = {
  'A01-BBI': 'บิฟิโดแบคทีเรียม บิฟิดัม',
  'A02-BBR': 'บิฟิโดแบคทีเรียม เบรเว',
  'A03-BLA': 'บิฟิโดแบคทีเรียม แล็กทิส',
  'A04-BLO': 'บิฟิโดแบคทีเรียม ลองกัม',
  'A05-LAC': 'แล็กโทบาซิลลัส แอซิโดฟิลัส',
  'A06-LGA': 'แล็กโทบาซิลลัส แกสเซอรี',
  'A07-LPA': 'แล็กโทบาซิลลัส พาราคาเซอิ',
  'A08-LRE': 'แล็กโทบาซิลลัส รียูเทอรี',
  'A09-LRH': 'แล็กโทบาซิลลัส รามโนซัส',
  'B01-LHE': 'แล็กโทบาซิลลัส เฮลเวทิคัส',
  'B02-LPL': 'แล็กโทบาซิลลัส แพลนทารัม',
  'B03-STH': 'สเตรปโตค็อกคัส เทอร์โมฟิลัส',
  'B04-AMU': 'แอกเคอร์แมนเซีย มิวซินิฟิลา'
};

// Static labels for the card, per language.
export const newformula_card_labels = {
  eng: {
    subtitle: 'Personalized Probiotics',
    tagline: 'Where microbiome data becomes personal care',
    activeStrain: 'Active Strain',
    supplementFacts: 'Supplement Facts',
    servingSize: 'Serving size',
    per: 'per',
    calories: 'Calories',
    protein: 'Protein',
    totalFat: 'Total Fat',
    totalCarbohydrate: 'Total Carbohydrate',
    sodium: 'Sodium',
    otherIngredients: 'Other Ingredients',
    // Short forms used under each nutrition chip.
    short: {
      calories: 'Kcal',
      protein: 'Protein',
      totalFat: 'Fat',
      totalCarbohydrate: 'Carbs',
      sodium: 'Sodium'
    }
  },
  thai: {
    subtitle: 'โพรไบโอติกเฉพาะบุคคล',
    tagline: 'Where microbiome data becomes personal care',
    activeStrain: 'สายพันธุ์หลัก',
    supplementFacts: 'ข้อมูลโภชนาการ',
    servingSize: 'หนึ่งหน่วยบริโภค',
    per: 'ต่อ',
    calories: 'พลังงาน',
    protein: 'โปรตีน',
    totalFat: 'ไขมันทั้งหมด',
    totalCarbohydrate: 'คาร์โบไฮเดรตทั้งหมด',
    sodium: 'โซเดียม',
    otherIngredients: 'ส่วนประกอบอื่น',
    // Short forms used under each nutrition chip.
    short: {
      calories: 'พลังงาน',
      protein: 'โปรตีน',
      totalFat: 'ไขมัน',
      totalCarbohydrate: 'คาร์บ',
      sodium: 'โซเดียม'
    }
  }
};

export const newformula_facts_9P = {
  'A01-BBI': {
    color: '#7a3f8f',
    activeStrain: 'Bifidobacterium bifidum',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.1 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '1 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A02-BBR': {
    color: '#8aa9d8',
    activeStrain: 'Bifidobacterium breve',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '3.3 Kcal',
      protein: '0.2 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.3 g',
      sodium: '1.3 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A03-BLA': {
    color: '#aeb863',
    activeStrain: 'Bifidobacterium lactis',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.5 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.7 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A04-BLO': {
    color: '#9ea098',
    activeStrain: 'Bifidobacterium longum',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.2 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.5 g',
      sodium: '0.8 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A05-LAC': {
    color: '#017440',
    activeStrain: 'Lactobacillus acidophilus',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.1 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A06-LGA': {
    color: '#fcc3c2',
    activeStrain: 'Lactobacillus gasseri',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.3 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A07-LPA': {
    color: '#ffc000',
    activeStrain: 'Lactobacillus paracasei',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.5 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.7 g',
      sodium: '0.7 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A08-LRE': {
    color: '#d32126',
    activeStrain: 'Lactobacillus reuteri',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.1 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'A09-LRH': {
    color: '#243874',
    activeStrain: 'Lactobacillus rhamnosus',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.2 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.7 g',
      sodium: '1 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'B01-LHE': {
    color: '#039d99',
    activeStrain: 'Lactobacillus helveticus',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.2 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '1.1 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'B02-LPL': {
    color: '#8d7159',
    activeStrain: 'Lactobacillus plantarum',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.3 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.7 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'B03-STH': {
    color: '#ae0580',
    activeStrain: 'Streptococcus thermophilus',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.1 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.6 g',
      sodium: '0.9 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  },
  'B04-AMU': {
    color: '#fe7b13',
    activeStrain: 'Akkermansia muciniphila',
    servingSize: '1 stick (2.0 g)',
    nutrition: {
      calories: '4.4 Kcal',
      protein: '0.1 g',
      totalFat: '0.0 g',
      totalCarbohydrate: '1.7 g',
      sodium: '1 mg'
    },
    otherIngredients:
      'Heat-killed Lactobacillus plantarum, galactooligosaccharides (GOS), ' +
      'inulin, fructooligosaccharides (FOS), silicon dioxide, tapioca fiber'
  }
};
